/**
 * NotesTab — the drafting agents' workspace notes, read-only.
 *
 * The lead and child drafters keep a scratchpad that outlives one section:
 * `decisions/register` (every value shared across sections, with the document
 * and page it was read from), `evidence/…` pointers and a `log`. Notes belong to
 * the document (project) — shared by all its sections and runs, never by other
 * documents in its folder — so this tab is document-scoped, not section-scoped.
 *
 * Data: getDocumentNotes(docId). Fetched on open and re-polled while the tab is
 * visible (notes change while a run drafts); the last good payload is kept
 * through a failed poll.
 */

import { Spinner, makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useState } from "react";
import { type DocumentNotesResponse, type WorkspaceNote, getDocumentNotes } from "../../../api/client";
import { getCachedDocId } from "../../services/documentSessionService";
import { fonts, inkColors, statusColors, surfaceColors, typeScale } from "../../tokens";
import AssistantMarkdown from "../AssistantMarkdown";

export const NOTES_POLL_MS = 10_000;
const REGISTER_KEY = "decisions/register";
/** What the drafters established about the document's subject — its identifiers
 *  (verified in FDA's databases) and the guidance found, read and stored for it. */
const SUBJECT_KEY = "subject/guidance";
/** Shown first and open, in this order. */
const PINNED = [SUBJECT_KEY, REGISTER_KEY];
const TITLES: Record<string, string> = { [SUBJECT_KEY]: "What Asthra established" };

// ---------------------------------------------------------------------------
// Pure helpers — exported for tests
// ---------------------------------------------------------------------------

/** What Asthra established, then the register (it is what keeps sections consistent), then by key. */
export function orderNotes(notes: WorkspaceNote[]): WorkspaceNote[] {
  const rank = (key: string) => {
    const i = PINNED.indexOf(key);
    return i === -1 ? PINNED.length : i;
  };
  return [...notes].sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key));
}

/** The heading a note is shown under: a title in the writer's terms where it has one, else its key. */
export function noteTitle(key: string): string {
  return TITLES[key] ?? key;
}

/** Whose notes these are, in the writer's terms. */
export function scopeCaption(scope: DocumentNotesResponse["scope"] | null | undefined): string {
  if (!scope) return "";
  if (scope.kind === "document") return "Notes for this document.";
  return "Notes for this run.";
}

export function formatUpdated(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: { padding: "12px 16px", overflowY: "auto", height: "100%" },
  meta: { fontSize: typeScale.code, color: inkColors.ink3, fontFamily: fonts.body, marginBottom: "10px" },
  note: {
    marginBottom: "12px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: surfaceColors.line,
    paddingBottom: "8px",
  },
  summary: { cursor: "pointer", fontFamily: fonts.body, fontSize: typeScale.body, color: inkColors.ink },
  key: { fontFamily: fonts.mono, fontWeight: 600 },
  stamp: { marginLeft: "8px", fontSize: typeScale.code, color: inkColors.ink3 },
  body: { marginTop: "6px" },
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "8px",
    padding: "32px 16px",
    color: inkColors.ink3,
    fontSize: typeScale.body,
    fontFamily: fonts.body,
    textAlign: "center",
  },
  errorText: { color: statusColors.danger },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface NotesTabProps {
  /** Override for tests / callers that already know it; defaults to the open document. */
  docId?: string;
}

const NotesTab: React.FC<NotesTabProps> = ({ docId }) => {
  const styles = useStyles();
  const id = docId ?? getCachedDocId();
  const [payload, setPayload] = useState<DocumentNotesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let controller: AbortController | null = null;
    const load = async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const resp = await getDocumentNotes(id, controller.signal);
        if (!cancelled) {
          setPayload(resp);
          setError(null);
        }
      } catch (e) {
        // Keep the last good payload; only an empty tab shows the error.
        if (!cancelled && !(e instanceof DOMException && e.name === "AbortError")) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    const timer = setInterval(() => void load(), NOTES_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
      controller?.abort();
    };
  }, [id]);

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spinner size="tiny" />
        <span>Loading notes…</span>
      </div>
    );
  }
  if (!id) {
    return (
      <div className={styles.centered}>
        <span>Notes are available once this document has been saved.</span>
      </div>
    );
  }
  if (!payload) {
    return (
      <div className={styles.centered}>
        <span className={styles.errorText}>Could not load notes: {error}</span>
      </div>
    );
  }
  if (payload.notes.length === 0) {
    return (
      <div className={styles.centered}>
        <span>No workspace notes yet.</span>
        <span>
          While drafting, the agents record values shared across sections here — each with the document and page it came
          from — so every section states them the same way.
        </span>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.meta}>
        {scopeCaption(payload.scope)} {payload.notes.length} note{payload.notes.length === 1 ? "" : "s"}.
      </div>
      {orderNotes(payload.notes).map((note) => (
        <details key={note.key} className={styles.note} open={PINNED.includes(note.key)}>
          <summary className={styles.summary}>
            <span className={styles.key}>{noteTitle(note.key)}</span>
            <span className={styles.stamp}>
              v{note.version}
              {formatUpdated(note.updated_at) ? ` · ${formatUpdated(note.updated_at)}` : ""}
            </span>
          </summary>
          <AssistantMarkdown className={styles.body} text={note.content} />
        </details>
      ))}
    </div>
  );
};

export default NotesTab;
